/**
 * Timekeeping Service API
 * Time entries, employees, projects, and reporting endpoints
 *
 * ⚠ TWENTY-TWO OF THE METHODS BELOW TARGET ENDPOINTS THAT DO NOT EXIST.
 *
 * Measured 2026-09-06 against `timekeeping/backend/app/main.py`'s `include_router` list, matching
 * on the HTTP VERB as well as the path — a `PUT /time-entries/{id}` is not served by a `GET` route
 * on the same path, and comparing paths alone hides exactly that.
 *
 * There is NO employees router, NO projects router, NO dashboard router and NO reports router in
 * this service, so all four sections so marked below answer 404, every one of them, always. Their
 * data belongs to other modules and the screens that needed it now read it from there:
 *
 *     employees → `hrService.getEmployees` / `pickEmployees`
 *     projects  → `projectsService.getProjects`
 *
 * FIVE METHODS THAT USED TO BE DEAD NOW HAVE A SERVER, mounted in
 * `api/routes/time_entry_writes.py`: `createTimeEntry`, `updateTimeEntry`, `deleteTimeEntry`,
 * `processApproval`, and — through `bulkAPI.submit` rather than through this client —
 * the paper-timesheet submit that used to call `daily-timesheet`.
 *
 * Everything still marked below has no server AND no caller. Left in place and labelled rather than
 * deleted, so the next person reading this file cannot conclude the endpoints exist.
 */

import { createAPIClient } from '../client'
import { API_CONFIG } from '../config'

const client = createAPIClient(API_CONFIG.TIMEKEEPING.baseURL, API_CONFIG.TIMEKEEPING.timeout)

export const timekeepingService = {
  // ==================== Time Entries ====================

  /**
   * Get all time entries with optional filters
   * @param {object} params - Query parameters
   * @returns {Promise} Array of time entries
   */
  getTimeEntries: async (params = {}) => {
    const response = await client.get('/time-entries/', { params })
    return response.data
  },

  /**
   * Get time entry by ID
   * @param {number} id - Time entry ID
   * @returns {Promise} Time entry data
   */
  getTimeEntry: async (id) => {
    const response = await client.get(`/time-entries/${id}`)
    return response.data
  },

  /**
   * Create new time entry
   * @param {object} data - Time entry data
   * @returns {Promise} Created time entry
   */
  // ✔ `POST /time-entries` — api/routes/time_entry_writes.py. No trailing slash: the route is
  // `@router.post("")`, and relying on FastAPI's 307 to carry a POST body is not a thing to
  // depend on.
  createTimeEntry: async (data) => {
    const response = await client.post('/time-entries', data)
    return response.data
  },

  /**
   * Update time entry
   * @param {number} id - Time entry ID
   * @param {object} data - Updated time entry data
   * @returns {Promise} Updated time entry
   */
  // ✔ `PUT /time-entries/{id}` — refuses a LOCKED row and a row generated by a daily report;
  // those are corrected by amending the report, or payroll and the report stop agreeing.
  updateTimeEntry: async (id, data) => {
    const response = await client.put(`/time-entries/${id}`, data)
    return response.data
  },

  /**
   * Delete time entry
   * @param {number} id - Time entry ID
   * @returns {Promise}
   */
  // ✔ `DELETE /time-entries/{id}` — same two refusals, and only while the row is pending.
  deleteTimeEntry: async (id) => {
    const response = await client.delete(`/time-entries/${id}`)
    return response.data
  },

  /**
   * Approve or reject time entry
   * @param {number} id - Time entry ID
   * @param {object} approvalData - Approval data
   * @returns {Promise}
   */
  // ✔ `POST /time-entries/{id}/approve` — takes `{action: 'approve'|'reject', reason}`, which
  // is the body both screens already sent. A rejection without a reason is refused: the person
  // whose hours these are will ask why.
  processApproval: async (id, approvalData) => {
    const response = await client.post(`/time-entries/${id}/approve`, approvalData)
    return response.data
  },

  /**
   * Get approval history for time entry
   * @param {number} id - Time entry ID
   * @returns {Promise} Approval history
   */
  // ⚠ 404: `GET /time-entries/{id}/approval-history` is not a route. The time-entries router is
  // read-only and serves "", "/summary/totals" and "/{entry_id}" only.
  getApprovalHistory: async (id) => {
    const response = await client.get(`/time-entries/${id}/approval-history`)
    return response.data
  },

  /**
   * Update per diem override
   * @param {number} id - Time entry ID
   * @param {object} perDiemData - Per diem data
   * @returns {Promise}
   */
  // ⚠ 404: `PUT /time-entries/{id}/per-diem` is not a route. The time-entries router is
  // read-only and serves "", "/summary/totals" and "/{entry_id}" only.
  updatePerDiem: async (id, perDiemData) => {
    const response = await client.put(`/time-entries/${id}/per-diem`, perDiemData)
    return response.data
  },

  /**
   * Get time entries summary with totals
   * @param {object} params - Query parameters
   * @returns {Promise} Summary data
   */
  getSummary: async (params = {}) => {
    const response = await client.get('/time-entries/summary/totals', { params })
    return response.data
  },

  getDailyReports: async (params = {}) => {
    const response = await client.get('/daily-reports', { params })
    return response.data
  },

  /**
   * Create daily timesheet (bulk entry)
   * @param {object} data - Daily timesheet data
   * @returns {Promise}
   */
  // ⚠ 404, and NOT worth building: `POST /time-entries/bulk` already does this, is gated on
  // `tk:proxy`, and runs every row through the same validator and the same per-diem and
  // overtime logic. `TimekeeperEntry.jsx` uses `bulkAPI.submit` now. This wrapper has no
  // callers and is kept only so the next reader sees why.
  createDailyTimesheet: async (data) => {
    const response = await client.post('/time-entries/daily-timesheet', data)
    return response.data
  },

  /**
   * Get daily breakdown
   * @param {object} params - Query parameters
   * @returns {Promise} Daily breakdown data
   */
  // ⚠ 404: `GET /time-entries/daily-breakdown` is not a route. The time-entries router is
  // read-only and serves "", "/summary/totals" and "/{entry_id}" only.
  getDailyBreakdown: async (params = {}) => {
    const response = await client.get('/time-entries/daily-breakdown', { params })
    return response.data
  },

  /**
   * Bulk approve entries
   * @param {object} params - Query parameters
   * @returns {Promise}
   */
  // ⚠ 404: `POST /time-entries/bulk-approve` is not a route. The time-entries router is
  // read-only and serves "", "/summary/totals" and "/{entry_id}" only.
  bulkApprove: async (params = {}) => {
    const response = await client.post('/time-entries/bulk-approve', null, { params })
    return response.data
  },

  // ==================== Employees ====================
  //
  // ⚠ NO EMPLOYEES ROUTER EXISTS IN THIS SERVICE. Every method in this section answers 404.
  // `getEmployees` is called by TimekeeperEntry.jsx, Export.jsx and TimeEntryEditModal.jsx —
  // three live screens. The employee list those screens want is HR's
  // (`hrService.getEmployees` / `pickEmployees`).

  /**
   * Get all employees
   * @param {object} params - Query parameters
   * @returns {Promise} Array of employees
   */
  getEmployees: async (params = {}) => {
    const response = await client.get('/employees', { params })
    return response.data
  },

  /**
   * Get employee by ID
   * @param {number} id - Employee ID
   * @returns {Promise} Employee data
   */
  getEmployee: async (id) => {
    const response = await client.get(`/employees/${id}`)
    return response.data
  },

  /**
   * Create new employee
   * @param {object} data - Employee data
   * @returns {Promise} Created employee
   */
  createEmployee: async (data) => {
    const response = await client.post('/employees', data)
    return response.data
  },

  /**
   * Update employee
   * @param {number} id - Employee ID
   * @param {object} data - Updated employee data
   * @returns {Promise} Updated employee
   */
  updateEmployee: async (id, data) => {
    const response = await client.put(`/employees/${id}`, data)
    return response.data
  },

  /**
   * Delete employee
   * @param {number} id - Employee ID
   * @returns {Promise}
   */
  deleteEmployee: async (id) => {
    const response = await client.delete(`/employees/${id}`)
    return response.data
  },

  /**
   * Get employee time summary
   * @param {number} id - Employee ID
   * @param {object} params - Query parameters
   * @returns {Promise} Time summary
   */
  getEmployeeTimeSummary: async (id, params = {}) => {
    const response = await client.get(`/employees/${id}/time-summary`, { params })
    return response.data
  },

  // ==================== Projects ====================
  //
  // ⚠ NO PROJECTS ROUTER EXISTS IN THIS SERVICE. Every method here answers 404.
  // `getProjects` is called by TimeEntryEditModal.jsx. Projects belong to the projects
  // module (`projectsAPI`), which is where every working screen reads them from.

  /**
   * Get all projects
   * @param {object} params - Query parameters
   * @returns {Promise} Array of projects
   */
  getProjects: async (params = {}) => {
    const response = await client.get('/projects', { params })
    return response.data
  },

  /**
   * Get project by ID
   * @param {number} id - Project ID
   * @returns {Promise} Project data
   */
  getProject: async (id) => {
    const response = await client.get(`/projects/${id}`)
    return response.data
  },

  /**
   * Create new project
   * @param {object} data - Project data
   * @returns {Promise} Created project
   */
  createProject: async (data) => {
    const response = await client.post('/projects', data)
    return response.data
  },

  /**
   * Update project
   * @param {number} id - Project ID
   * @param {object} data - Updated project data
   * @returns {Promise} Updated project
   */
  updateProject: async (id, data) => {
    const response = await client.put(`/projects/${id}`, data)
    return response.data
  },

  /**
   * Delete project
   * @param {number} id - Project ID
   * @returns {Promise}
   */
  deleteProject: async (id) => {
    const response = await client.delete(`/projects/${id}`)
    return response.data
  },

  /**
   * Get project time summary
   * @param {number} id - Project ID
   * @param {object} params - Query parameters
   * @returns {Promise} Time summary
   */
  getProjectTimeSummary: async (id, params = {}) => {
    const response = await client.get(`/projects/${id}/time-summary`, { params })
    return response.data
  },

  // ==================== Dashboard ====================
  //
  // ⚠ NO DASHBOARD ROUTER EXISTS IN THIS SERVICE. Every method here answers 404, and none
  // has a caller — the timekeeping dashboard builds its figures from the reads that do work.

  /**
   * Get dashboard summary data
   * @param {object} params - Query parameters
   * @returns {Promise} Dashboard summary
   */
  getDashboardSummary: async (params = {}) => {
    const response = await client.get('/dashboard/summary', { params })
    return response.data
  },

  /**
   * Get recent time entries
   * @param {number} limit - Number of entries to fetch
   * @returns {Promise} Recent entries
   */
  getRecentEntries: async (limit = 10) => {
    const response = await client.get(`/dashboard/recent-entries?limit=${limit}`)
    return response.data
  },

  /**
   * Get time statistics
   * @param {object} params - Query parameters
   * @returns {Promise} Time statistics
   */
  getTimeStats: async (params = {}) => {
    const response = await client.get('/dashboard/time-stats', { params })
    return response.data
  },

  // ==================== Reports ====================
  //
  // ⚠ NO REPORTS ROUTER EXISTS IN THIS SERVICE. Every method here answers 404, and none has
  // a caller. Export.jsx builds its files from `daily-reports` and `employee-hours`.

  /**
   * Generate timesheet report
   * @param {object} params - Report parameters
   * @returns {Promise} File blob
   */
  generateTimesheetReport: async (params = {}) => {
    const response = await client.post('/reports/timesheet', params, {
      responseType: 'blob'
    })
    return response.data
  },

  /**
   * Generate payroll report
   * @param {object} params - Report parameters
   * @returns {Promise} File blob
   */
  generatePayrollReport: async (params = {}) => {
    const response = await client.post('/reports/payroll', params, {
      responseType: 'blob'
    })
    return response.data
  },

  /**
   * Generate project report
   * @param {object} params - Report parameters
   * @returns {Promise} File blob
   */
  generateProjectReport: async (params = {}) => {
    const response = await client.post('/reports/project', params, {
      responseType: 'blob'
    })
    return response.data
  },

  /**
   * Get recent exports
   * @returns {Promise} Array of recent exports
   */
  getRecentExports: async () => {
    const response = await client.get('/reports/recent')
    return response.data
  },

  // ==================== Rate Sheets ====================

  /**
   * Get all rate sheets
   */
  getRateSheets: async (params = {}) => {
    const response = await client.get('/rate-sheets', { params })
    return response.data
  },

  /**
   * Get rate sheet by ID
   */
  getRateSheet: async (id) => {
    const response = await client.get(`/rate-sheets/${id}`)
    return response.data
  },

  /**
   * Create new rate sheet
   */
  createRateSheet: async (data) => {
    const response = await client.post('/rate-sheets', data)
    return response.data
  },

  /**
   * Update rate sheet
   */
  updateRateSheet: async (id, data) => {
    const response = await client.put(`/rate-sheets/${id}`, data)
    return response.data
  },

  /**
   * Delete rate sheet
   */
  deleteRateSheet: async (id) => {
    const response = await client.delete(`/rate-sheets/${id}`)
    return response.data
  },

  /**
   * Clone rate sheet
   */
  cloneRateSheet: async (id) => {
    const response = await client.post(`/rate-sheets/${id}/clone`)
    return response.data
  },

  /**
   * Generate Rate Sheet from Global template
   */
  generateRateSheetFromGlobal: async () => {
    const response = await client.post(`/rate-sheets/from-global`)
    return response.data
  },
}

/**
 * Utility function to handle file downloads
 * @param {Blob} blob - File blob
 * @param {string} filename - Filename for download
 */
export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export const viewFile = (blob) => {
  const url = window.URL.createObjectURL(blob)
  window.open(url, '_blank')
  setTimeout(() => window.URL.revokeObjectURL(url), 60000)
}
